import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X, ArrowRight, Users, CalendarDays, Phone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * Mundalo - Guided → Connected upsell nudge.
 *
 * Renders only when the authenticated user's role === 'guided'. Hidden for
 * Connected and all other tiers. Per-session dismissal via sessionStorage -
 * comes back on the next browser session.
 *
 * Variants:
 *   • banner      - full-width dashboard banner, gold accent
 *   • card        - subtle cream card for in-page tops (Community/Events/Housing)
 *   • call-upsell - Schedule-a-Call follow-up nudge with two CTAs
 *
 * Pass a unique `storageKey` per surface so dismissals don't bleed across pages.
 */
const COPY = {
  banner: {
    icon: Sparkles,
    eyebrow: "You're on Guided",
    headline: 'Unlock monthly Expert Q&A with Connected.',
    body:
      'Connected adds live expert Q&A sessions and priority access to events - ' +
      'for members who want the full Mundalo community, not just the move.',
    cta: 'See Connected',
  },
  card: {
    eyebrow: 'Want more?',
    headline: 'Connected adds the parts of Mundalo Guided doesn’t cover.',
    body:
      'Monthly Expert Q&A · Priority events. Same toolkit, plus the community ' +
      'that keeps moving with you after week one.',
    cta: 'Compare Connected →',
  },
  'call-upsell': {
    eyebrow: 'Used your included call?',
    headline: 'Keep the momentum going.',
    body:
      'Two ways to go deeper: buy another $99 call right now, or upgrade to ' +
      'Connected for live monthly Q&A with our experts.',
    cta: 'Compare Connected',
    secondaryCta: 'Buy another $99 call',
  },
};

const dismissedKey = (key) => `mundalo:upsell:dismissed:${key}`;

const GuidedUpsell = ({
  variant = 'card',
  storageKey,
  onBuyAdditionalCall, // call-upsell only
  className = '',
}) => {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const [dismissed, setDismissed] = useState(false);

  // Hydrate dismissal state once per surface
  useEffect(() => {
    if (!storageKey) return;
    try {
      if (sessionStorage.getItem(dismissedKey(storageKey)) === '1') {
        setDismissed(true);
      }
    } catch {
      /* sessionStorage unavailable - fall through, will just always show */
    }
  }, [storageKey]);

  // Hard gate - Guided only. Admins/Connected/all other tiers never see this.
  if (role !== 'guided') return null;
  if (dismissed) return null;
  if (!COPY[variant]) return null;

  const copy = COPY[variant];

  const handleDismiss = () => {
    setDismissed(true);
    if (storageKey) {
      try { sessionStorage.setItem(dismissedKey(storageKey), '1'); } catch {}
    }
  };

  if (variant === 'banner') {
    return (
      <div
        className={`relative rounded-2xl overflow-hidden mb-6 ${className}`}
        data-testid={`guided-upsell-banner-${storageKey || 'default'}`}
      >
        <div className="bg-[#0A1F2E] text-[#F9F6EF]">
          <div className="px-5 md:px-7 py-5 md:py-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
            <div className="flex items-start gap-3 flex-1">
              <span className="hidden md:flex items-center justify-center w-10 h-10 rounded-full bg-[#E8A830] text-[#0A1F2E] shrink-0">
                <Sparkles className="w-5 h-5" />
              </span>
              <div className="min-w-0">
                <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#E8A830] mb-1">
                  {copy.eyebrow}
                </p>
                <h3 className="font-display italic text-xl md:text-2xl leading-tight mb-1">
                  {copy.headline}
                </h3>
                <p className="text-sm text-[#F9F6EF]/75 max-w-2xl">
                  {copy.body}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 md:shrink-0">
              <Link
                to="/portal/upgrade?from=dashboard-banner&to=connected"
                className="inline-flex items-center gap-2 bg-[#E8A830] hover:bg-[#d59824] text-[#0A1F2E] font-mono uppercase text-[10px] md:text-[11px] tracking-[0.15em] px-4 md:px-5 py-2.5 rounded-full transition"
                data-testid={`guided-upsell-cta-${storageKey || 'default'}`}
              >
                {copy.cta} <ArrowRight className="w-4 h-4" />
              </Link>
              <button
                type="button"
                onClick={handleDismiss}
                className="text-[#F9F6EF]/55 hover:text-[#F9F6EF] transition"
                aria-label="Dismiss for this session"
                data-testid={`guided-upsell-dismiss-${storageKey || 'default'}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'call-upsell') {
    return (
      <aside
        className={`relative rounded-2xl bg-[#F4ECD8] border border-[#E8A830]/40 p-5 md:p-6 mb-6 ${className}`}
        data-testid={`guided-upsell-call-${storageKey || 'default'}`}
      >
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-[#0A1F2E]/40 hover:text-[#0A1F2E]"
          aria-label="Dismiss for this session"
          data-testid={`guided-upsell-dismiss-${storageKey || 'default'}`}
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 max-w-3xl">
          <span className="hidden md:flex items-center justify-center w-9 h-9 rounded-full bg-[#E8A830] text-[#0A1F2E] shrink-0">
            <Phone className="w-4 h-4" />
          </span>
          <div className="flex-1">
            <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-1">
              {copy.eyebrow}
            </p>
            <h3 className="font-display italic text-xl text-[#0A1F2E] mb-1">
              {copy.headline}
            </h3>
            <p className="text-sm text-[#0A1F2E]/75 mb-4">
              {copy.body}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/portal/upgrade?from=call-upsell&to=connected"
                className="inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[10px] tracking-[0.15em] px-4 py-2.5 rounded-full transition"
                data-testid={`guided-upsell-primary-${storageKey || 'default'}`}
              >
                {copy.cta} <ArrowRight className="w-4 h-4" />
              </Link>
              {onBuyAdditionalCall && (
                <button
                  type="button"
                  onClick={onBuyAdditionalCall}
                  className="inline-flex items-center gap-2 bg-white hover:bg-[#F9F6EF] border border-[#0A1F2E]/15 text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.15em] px-4 py-2.5 rounded-full transition"
                  data-testid={`guided-upsell-secondary-${storageKey || 'default'}`}
                >
                  {copy.secondaryCta}
                </button>
              )}
            </div>
          </div>
        </div>
      </aside>
    );
  }

  // Default: card variant - cream, subtle, in-page top
  return (
    <aside
      className={`relative rounded-2xl bg-[#F4ECD8] border border-[#EFE6CF] p-4 md:p-5 mb-6 ${className}`}
      data-testid={`guided-upsell-card-${storageKey || 'default'}`}
    >
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-[#0A1F2E]/40 hover:text-[#0A1F2E]"
        aria-label="Dismiss for this session"
        data-testid={`guided-upsell-dismiss-${storageKey || 'default'}`}
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-7">
        <span className="hidden md:flex items-center justify-center w-8 h-8 rounded-full bg-white border border-[#E8A830]/40 text-[#E8A830] shrink-0">
          <Sparkles className="w-4 h-4" />
        </span>
        <div className="flex-1">
          <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-1">
            {copy.eyebrow}
          </p>
          <p className="font-display italic text-lg md:text-xl text-[#0A1F2E] leading-snug mb-1">
            {copy.headline}
          </p>
          <p className="text-sm text-[#0A1F2E]/70 mb-3">{copy.body}</p>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={`/portal/upgrade?from=${storageKey || 'card'}&to=connected`}
              className="inline-flex items-center gap-1.5 text-[#1A5C6E] hover:text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.15em] transition"
              data-testid={`guided-upsell-cta-${storageKey || 'default'}`}
            >
              {copy.cta}
            </Link>
            <span className="hidden md:inline-flex items-center gap-3 text-[10px] text-[#0A1F2E]/55 font-mono uppercase tracking-[0.15em]">
              <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Expert Q&A</span>
              <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> Priority events</span>
            </span>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default GuidedUpsell;
